import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { Section } from '../settingsPrimitives';
import { useNotificationStore } from '../../store/notificationStore';
import { isTauri } from '../../utils/tauri';
import './SshHostKeys.css';

function formatWhen(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return '';
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return '';
  }
}

/**
 * Settings → Extensions → Terminal / FTP → **Known hosts**: the SSH servers this
 * account has trusted on this device (host, key fingerprint, when). Remove is
 * the ONE way past a changed-key refusal: the next connect asks again and shows
 * the new fingerprint.
 */
export default function KnownHostsSettings() {
  const { t } = useTranslation();
  const [hosts, setHosts] = useState([]);

  const refresh = useCallback(async () => {
    if (!isTauri()) return;
    try {
      const list = await invoke('ssh_known_hosts_list');
      setHosts(Array.isArray(list) ? list : []);
    } catch (e) {
      useNotificationStore.getState().error(String(e?.message || e));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const remove = useCallback(
    async (h) => {
      try {
        await invoke('ssh_known_hosts_remove', { host: h.host, port: h.port });
        useNotificationStore.getState().success(
          t('sshHostKeys.removed', 'Saved key for {{host}} removed.', {
            host: `${h.host}:${h.port}`,
          })
        );
      } catch (e) {
        useNotificationStore.getState().error(String(e?.message || e));
      }
      refresh();
    },
    [refresh, t]
  );

  return (
    <Section title={t('sshHostKeys.settingsTitle', 'Known hosts')}>
      <div className="settings-row-desc" style={{ padding: '4px 0 8px' }}>
        {t(
          'sshHostKeys.settingsDesc',
          'SSH servers you have trusted on this device. If a server’s key changes, connections are refused until you remove its saved key here.'
        )}
      </div>
      {hosts.length === 0 ? (
        <div className="settings-row-desc" data-testid="known-hosts-empty">
          {t('sshHostKeys.settingsEmpty', 'No trusted servers yet.')}
        </div>
      ) : (
        <ul className="ssh-known-hosts" data-testid="known-hosts-list">
          {hosts.map((h) => (
            <li key={`${h.host}:${h.port}`} className="ssh-known-hosts-row">
              <div className="ssh-known-hosts-main">
                <div className="ssh-known-hosts-host">
                  {h.host}:{h.port}
                </div>
                <div className="ssh-hostkey-fp">
                  {h.keyType} {h.fingerprint}
                </div>
                <div className="ssh-known-hosts-meta">
                  {t('sshHostKeys.addedAt', 'Trusted {{when}}', { when: formatWhen(h.addedAtMs) })}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(h)}
                aria-label={t('sshHostKeys.removeAria', 'Remove saved key for {{host}}', {
                  host: `${h.host}:${h.port}`,
                })}
                className="ssh-hostkey-btn px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
              >
                {t('sshHostKeys.remove', 'Remove')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
